<script setup lang="ts">
import { computed, inject, onMounted, useTemplateRef, watch } from "vue";
import type { BgImage, ControlSize, ModalPosition } from "../models/types";
import BcButton from "./bc-button.vue";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../models";

export type BcModalProps = {
  /** Changes modal to alert dialog */
  alertDialog?: boolean;
  /** Sets the button text for the alert dialog */
  alertOkText?: string;
  /** Specifies animation for the modal */
  animation?: string;
  /** Specifies background visibility for the modal */
  background?: boolean;
  /** Includes a backdrop layer to the background */
  backdrop?: boolean;
  /** Specifies background image for the modal */
  backgroundImage?: BgImage;
  /** Blur backdrop layer to the background */
  blur?: boolean;
  /** Defines the radius of the backdrop's blur */
  blurRadius?: number;
  /** Sets a border around the modal */
  bordered?: boolean;
  /** Defines the radius of the modal's corners */
  borderRadius?: string | number;
  /** Adds close icon to the header */
  closeIcon?: boolean;
  /** Changes modal to confirm dialog */
  confirmDialog?: boolean;
  /** Sets a divider for the footer */
  divideFooter?: boolean;
  /** Specifies the close behavior */
  closeOnClick?: boolean;
  /** Sets the `No` button text for the confirm dialog */
  confirmNoText?: string;
  /** Sets the `Yes` button text for the confirm dialog */
  confirmYesText?: string;
  /** Sets the dialog title added to the header */
  dialogTitle?: string;
  /** Sets a divider for the header */
  divideHeader?: boolean;
  /** Makes the modal dialog full screen */
  fullScreen?: boolean;
  /** Specifies the modal's height */
  height?: string | number;
  /** Specifies inner background visibility for the modal */
  innerBackground?: boolean;
  /** Specifies the modal's minimum height */
  minHeight?: string | number;
  /** Specifies the modal's minimum width */
  minWidth?: string | number;
  /** Sets the modal position */
  modalPosition?: ModalPosition;
  /** Makes modal border rounded */
  rounded?: boolean;
  /** Specifies box shadow for the modal */
  shadow?: boolean;
  /** Specifies pre-defined sizes for the modal */
  size?: ControlSize;
  /** Specifies the modal's width */
  width?: string | number;
};

export type BcModalEmits = {
  (event: "alert-ok"): void;
  (event: "confirm-no"): void;
  (event: "confirm-yes"): void;
  (event: "update:active", payload: boolean): void;
};

/** Specifies two-way binding (for visibility) for the modal */
const modelValue = defineModel<boolean>({ default: true });

const {
  alertDialog = false,
  alertOkText = "OK",
  animation = "zoom-out",
  background = true,
  backdrop = false,
  backgroundImage,
  blur = false,
  blurRadius = 4,
  bordered = true,
  borderRadius = 4,
  closeIcon = false,
  confirmDialog = false,
  divideFooter = false,
  closeOnClick = true,
  confirmNoText = "Cancel",
  confirmYesText = "OK",
  dialogTitle,
  divideHeader = false,
  fullScreen = false,
  height,
  innerBackground = false,
  minHeight,
  minWidth,
  modalPosition = "middle-center",
  rounded = false,
  shadow = false,
  size,
  width,
} = defineProps<BcModalProps>();

const emit = defineEmits<BcModalEmits>();

const dialogRef = useTemplateRef("dialogRef");
const dialogBodyRef = useTemplateRef("dialogBodyRef");
const modalControlRef = useTemplateRef("modalControlRef");

const dialogBorderRadius = computed<string>(() => {
  if (typeof borderRadius === "number") {
    return `${borderRadius}px`;
  }
  return borderRadius;
});

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

const positionClasses = computed(() => {
  return modalPosition.split("-");
});

function alertOkClicked(): void {
  emit("alert-ok");
  modelValue.value = false;
  emit("update:active", false);
}

function confirmYesClicked(): void {
  emit("confirm-yes");
  modelValue.value = false;
  emit("update:active", false);
}

function confirmNoClicked(): void {
  emit("confirm-no");
  modelValue.value = false;
  emit("update:active", false);
}

function closeIconClicked(): void {
  modelValue.value = false;
  emit("update:active", false);
}

function modalClicked(event: MouseEvent): void {
  if (
    modelValue.value &&
    closeOnClick &&
    dialogRef.value &&
    event.target !== dialogRef.value &&
    !dialogRef.value.contains(event.target as Node)
  ) {
    modelValue.value = false;
    emit("update:active", false);
  }
}

function setDialogHeight(): void {
  if (height == null) {
    modalControlRef.value?.style.setProperty("--modal_dialog_height", "unset");
    return;
  }

  const value = typeof height === "number" ? `${height}px` : height.replace(/\s/g, "");
  modalControlRef.value?.style.setProperty("--modal_dialog_height", value);
}

function setDialogWidth(): void {
  if (width == null) {
    modalControlRef.value?.style.setProperty("--modal_dialog_width", "unset");
    return;
  }

  const value = typeof width === "number" ? `${width}px` : width.replace(/\s/g, "");
  modalControlRef.value?.style.setProperty("--modal_dialog_width", value);
}

function setDialogMinHeight(): void {
  if (minHeight == null) {
    dialogRef.value?.style.removeProperty("min-height");
    return;
  }
  const value =
    typeof minHeight === "number" ? `${minHeight}px` : minHeight.replace(/\s/g, "");
  dialogRef.value?.style.setProperty("min-height", value);
}

function setDialogMinWidth(): void {
  if (minWidth == null) {
    dialogRef.value?.style.removeProperty("min-width");
    return;
  }

  const value =
    typeof minWidth === "number" ? `${minWidth}px` : minWidth.replace(/\s/g, "");
  dialogRef.value?.style.setProperty("min-width", value);
}

onMounted(() => {
  // Set size
  setDialogWidth();
  setDialogHeight();
  setDialogMinWidth();
  setDialogMinHeight();
});

watch(() => width, setDialogWidth);
watch(() => height, setDialogHeight);
watch(() => minWidth, setDialogMinWidth);
watch(() => minHeight, setDialogMinHeight);

defineExpose({
  modelValue,
  dialogRef,
  dialogBodyRef,
  modalControlRef,
});
</script>

<template>
  <transition v-bind:name="animation">
    <div
      ref="modalControlRef"
      v-bind:data-pak="pak"
      v-bind:data-design="design"
      class="bissantzControls modalControl"
      v-bind:class="[
        positionClasses,
        {
          active: modelValue,
          allowBackground: background,
        },
      ]"
      v-bind:data-background-image="backgroundImage"
      v-show="modelValue"
      v-on:click="modalClicked"
    >
      <div
        class="background"
        v-bind:class="{ blur: blur, backdrop: backdrop }"
        v-bind:style="{ backdropFilter: blur ? `blur(${blurRadius}px)` : undefined }"
      ></div>
      <div v-if="innerBackground" class="innerBackground"></div>

      <div
        ref="dialogRef"
        class="dialog"
        v-bind:class="[
          size ? `dialog-${size}` : '',
          {
            bordered: bordered,
            fullScreen: fullScreen,
            shadow: shadow,
          },
        ]"
        v-bind:style="{ borderRadius: rounded ? dialogBorderRadius : undefined }"
      >
        <header
          v-if="$slots.header || dialogTitle"
          class="dialogHeader"
          v-bind:class="{ divide: divideHeader }"
        >
          <slot name="header">
            <h5 class="dialogTitle">{{ dialogTitle }}</h5>
          </slot>
          <BcButton v-if="closeIcon" v-on:click="closeIconClicked" plain>🞪</BcButton>
        </header>
        <main
          v-if="$slots.main || $slots.body || $slots.default"
          ref="dialogBodyRef"
          class="dialogBody"
        >
          <slot v-if="$slots.main" name="main" />
          <slot v-else-if="$slots.body" name="body" />
          <slot v-else-if="$slots.default" />
        </main>
        <footer
          v-if="$slots.footer || alertDialog || confirmDialog"
          class="dialogFooter"
          v-bind:class="{ divide: divideFooter }"
        >
          <template v-if="alertDialog">
            <BcButton size="md" v-on:click="alertOkClicked">{{ alertOkText }}</BcButton>
          </template>
          <template v-else-if="confirmDialog">
            <BcButton size="md" v-on:click="confirmYesClicked">{{
              confirmYesText
            }}</BcButton>
            <BcButton size="md" v-on:click="confirmNoClicked">{{
              confirmNoText
            }}</BcButton>
          </template>
          <slot v-else name="footer" />
        </footer>
      </div>
    </div>
  </transition>
</template>

<style lang="less">
@import "../styles/components/bc-modal.less";
</style>
